import { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import RichEditor from "../components/RichEditor";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PRIORITIES, PRIORITY_LABEL, CATEGORIES, CATEGORY_LABEL } from "../lib/labels";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, Gauge, PaperPlaneTilt, Phone, Megaphone, Warning, Info, Paperclip, X, File as FileIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import ChatWidget from "../components/ChatWidget";

function AnnouncementBanner({ items, speed = 6 }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!items || items.length <= 1) return;
    const dwell = Math.max(3, Number(speed) || 6) * 1000;
    const fade = 500;
    const int = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        setVisible(true);
      }, fade);
    }, dwell);
    return () => clearInterval(int);
  }, [items, speed]);
  if (!items || items.length === 0) return null;
  const current = items[idx] || items[0];
  const bgFor = (sev) => {
    if (sev === "critical") return "bg-red-600 text-white";
    if (sev === "warning") return "bg-amber-500 text-white";
    return "bg-emerald-600 text-white";
  };
  const iconFor = (sev) => sev === "critical" ? <Warning weight="fill" size={16}/>
    : sev === "warning" ? <Warning size={16}/>
    : <Info weight="fill" size={16}/>;
  return (
    <div className={`w-full transition-colors duration-500 ${bgFor(current.severity)}`} data-testid="portal-announcements">
      <div
        key={current.id}
        className={`w-full py-2 px-4 text-center text-sm font-semibold flex items-center justify-center gap-2 transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
      >
        {iconFor(current.severity)}
        <span>{current.message}</span>
      </div>
    </div>
  );
}

export default function PublicPortal() {
  const [form, setForm] = useState({
    subject: "", description: "", category: "autre", priority: "normale",
    client_first_name: "", client_last_name: "",
    client_email: "", client_phone: "", company: "",
  });
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [phones, setPhones] = useState({ support_phone: "", virtual_assistant_phone: "" });
  const [announcements, setAnnouncements] = useState([]);
  const [announceSpeed, setAnnounceSpeed] = useState(30);
  const [remoteAssistUrl, setRemoteAssistUrl] = useState("");
  const [formAlign, setFormAlign] = useState("center");
  const [sideImage, setSideImage] = useState("");

  useEffect(() => {
    api.get(`/settings/portal/public?t=${Date.now()}`).then((r) => {
      setPhones({
        support_phone: r.data?.support_phone || "",
        virtual_assistant_phone: r.data?.virtual_assistant_phone || "",
      });
      setAnnouncements(r.data?.announcements || []);
      setAnnounceSpeed(r.data?.announcement_speed_seconds || 30);
      setRemoteAssistUrl(r.data?.remote_assist_url || "");
      setFormAlign(r.data?.form_alignment || "center");
      setSideImage(r.data?.form_side_image_url || "");
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation of the description (RichEditor stores HTML — check inner text)
    const plainDesc = (form.description || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
    if (!plainDesc) {
      toast.error("La description est obligatoire");
      return;
    }
    const firstName = form.client_first_name.trim();
    const lastName = form.client_last_name.trim();
    const fullName = `${firstName} ${lastName}`.trim();
    if (!firstName || !lastName) {
      toast.error("Prénom et nom sont obligatoires");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        client_name: fullName,
      };
      // Sentinel email when the user declared having no email → will suppress notifications automatically.
      if (payload.no_email || !payload.client_email) {
        const slug = fullName.toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/^\.+|\.+$/g, "") || "sans-email";
        payload.client_email = `${slug}.${Date.now().toString(36)}@no-email.masterticket.io`;
      }
      delete payload.client_first_name;
      delete payload.client_last_name;
      delete payload.no_email;
      const r = await api.post("/tickets/public", payload);
      const { id, number, upload_token } = r.data;
      // Upload attached files (best-effort — failures do not block ticket creation)
      if (files.length > 0 && upload_token) {
        for (const f of files) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/tickets/public/${id}/attachments?token=${upload_token}`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            toast.warning(`Fichier « ${f.name} » non joint : ${err.response?.data?.detail || "erreur"}`);
          }
        }
      }
      setSuccess(number);
      setFiles([]);
      toast.success(`Ticket #${number} créé${files.length ? ` avec ${files.length} pièce(s) jointe(s)` : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const onPickFiles = (fileList) => {
    if (!fileList?.length) return;
    const total = files.length + fileList.length;
    if (total > 10) { toast.error("Maximum 10 fichiers"); return; }
    setFiles((prev) => [...prev, ...Array.from(fileList)]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onChatEscalate = ({ category, transcript }) => {
    setForm((f) => ({
      ...f,
      category: category || f.category,
      subject: f.subject || (transcript.split("\n")[0] || "").replace(/^Moi:\s*/, "").slice(0, 100),
      description: (f.description
        ? f.description + "\n\n"
        : "") + `<hr/><p><em>Conversation avec l'Assistant IA :</em></p><pre style="white-space:pre-wrap;font-family:inherit;font-size:0.9em;">${transcript.replace(/</g, "&lt;")}</pre>`,
    }));
    // scroll to form
    setTimeout(() => document.querySelector("[data-testid='portal-form']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    toast.info("Conversation reprise dans le formulaire");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AnnouncementBanner items={announcements} speed={announceSpeed}/>
      <header className="border-b bg-white dark:bg-[hsl(222_20%_10%)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="portal-brand">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <Gauge weight="bold" size={20} className="text-white"/>
            </div>
            <div>
              <div className="font-display font-bold">MasterTicket</div>
              <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">Portail client</div>
            </div>
          </Link>
          {(phones.support_phone || phones.virtual_assistant_phone) && (
            <div className="hidden sm:flex items-center gap-5 text-sm">
              {phones.support_phone && (
                <a href={`tel:${phones.support_phone.replace(/\s+/g, "")}`}
                   data-testid="portal-phone-support"
                   className="inline-flex items-center gap-2 text-blue-700 dark:text-blue-300 hover:underline">
                  <Phone weight="fill" size={14}/>
                  <span className="text-muted-foreground">Ligne support :</span>
                  <span className="mono font-medium">{phones.support_phone}</span>
                </a>
              )}
              {phones.virtual_assistant_phone && (
                <a href={`tel:${phones.virtual_assistant_phone.replace(/\s+/g, "")}`}
                   data-testid="portal-phone-virtual"
                   className="inline-flex items-center gap-2 text-emerald-700 dark:text-emerald-300 hover:underline">
                  <Phone weight="fill" size={14}/>
                  <span className="text-muted-foreground">Assistant Virtuel 24h/24 :</span>
                  <span className="mono font-medium">{phones.virtual_assistant_phone}</span>
                </a>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="portal-login">
              <Button className="bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white">
                Se connecter
              </Button>
            </Link>
            {remoteAssistUrl && (
              <a href={remoteAssistUrl} target="_blank" rel="noopener noreferrer"
                 data-testid="portal-remote-assist"
                 title="Télémaintenance — cliquez pour lancer l'assistance à distance"
                 className="h-[30px] w-[30px] rounded-full overflow-hidden ring-2 ring-blue-500/40 hover:ring-blue-500 hover:scale-105 transition-all shadow-md">
                <img src="https://www.netkom.com/img/aide.jpg" alt="Télémaintenance NetKom" className="w-full h-full object-cover"/>
              </a>
            )}
          </div>
        </div>
      </header>

      <main className={`px-6 py-6 ${formAlign === "center" ? "max-w-5xl mx-auto" : "max-w-7xl mx-auto"}`}>
        {(() => {
          const formNode = success ? (
            <Card>
              <CardContent className="pt-10 pb-10 text-center">
                <CheckCircle weight="fill" size={64} className="text-emerald-500 mx-auto"/>
                <h1 className="font-display text-3xl font-bold mt-4">Votre demande est enregistrée</h1>
                <p className="text-muted-foreground mt-2">Numéro de ticket : <span className="mono font-semibold">#{success}</span></p>
                <p className="text-sm text-muted-foreground mt-1">Un e-mail de confirmation sera envoyé à <b>{form.client_email}</b>. Notre équipe prend en charge votre demande.</p>
                <Button className="mt-6" onClick={() => { setSuccess(null); setForm({ subject: "", description: "", category: "autre", priority: "normale", client_name: "", client_email: "", company: "" }); }} data-testid="portal-new">
                  Ouvrir une autre demande
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="mb-4">
                <div className="label-caps">Portail client</div>
                <h1 className="font-display text-2xl md:text-3xl font-bold mt-1">Ouvrir une demande d&apos;assistance</h1>
                <p className="text-muted-foreground mt-1 text-sm">
                  Décrivez votre problème le plus précisément possible. Notre équipe traite votre demande selon la priorité et vous répondra par e-mail.
                </p>
              </div>
              <Card data-testid="portal-form">
                <CardContent className="pt-5 pb-5">
                  <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Votre prénom *</Label>
                        <Input required data-testid="portal-firstname" value={form.client_first_name} onChange={(e) => setForm({...form, client_first_name: e.target.value})} placeholder="Ex. Jean"/>
                      </div>
                      <div>
                        <Label>Votre nom *</Label>
                        <Input required data-testid="portal-name" value={form.client_last_name} onChange={(e) => setForm({...form, client_last_name: e.target.value})} placeholder="Ex. Dupont"/>
                      </div>
                      <div>
                        <Label>Votre e-mail *</Label>
                        <Input
                          required={!form.no_email}
                          disabled={form.no_email}
                          type="email" data-testid="portal-email"
                          value={form.no_email ? "" : form.client_email}
                          onChange={(e) => setForm({...form, client_email: e.target.value})}
                          placeholder={form.no_email ? "(pas d'email fourni)" : "vous@entreprise.fr"}/>
                        <label className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground cursor-pointer">
                          <input type="checkbox" checked={!!form.no_email}
                            onChange={(e) => setForm({...form, no_email: e.target.checked, client_email: e.target.checked ? "" : form.client_email})}
                            className="accent-primary" data-testid="portal-no-email"/>
                          Je n&apos;ai pas d&apos;adresse email — je serai contacté par téléphone
                        </label>
                      </div>
                      <div>
                        <Label>Votre téléphone {form.no_email ? "*" : "(optionnel)"}</Label>
                        <Input required={!!form.no_email} type="tel" data-testid="portal-phone" value={form.client_phone} onChange={(e) => setForm({...form, client_phone: e.target.value})} placeholder="Ex. 04 74 12 34 56"/>
                      </div>
                      <div>
                        <Label>Société (optionnel)</Label>
                        <Input data-testid="portal-company" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})}/>
                      </div>
                      <div>
                        <Label>Sujet *</Label>
                        <Input required data-testid="portal-subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}/>
                      </div>
                      <div>
                        <Label>Catégorie</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                          <SelectTrigger data-testid="portal-category"><SelectValue/></SelectTrigger>
                          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Priorité perçue</Label>
                        <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                          <SelectTrigger data-testid="portal-priority"><SelectValue/></SelectTrigger>
                          <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Description détaillée *</Label>
                      <Textarea required data-testid="portal-description" className="hidden" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
                      <RichEditor value={form.description} onChange={(v) => setForm({...form, description: v})} minHeight={140}/>
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Paperclip size={14}/> Pièces jointes (optionnel — max 10 fichiers)
                      </Label>
                      <input ref={fileInputRef} type="file" multiple className="hidden"
                        data-testid="portal-file-input"
                        onChange={(e) => onPickFiles(e.target.files)}/>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Button type="button" size="sm" variant="secondary"
                          onClick={() => fileInputRef.current?.click()}
                          data-testid="portal-file-btn">
                          <Paperclip size={14} className="mr-1"/> Ajouter des fichiers
                        </Button>
                        {files.length === 0 && (
                          <span className="text-xs text-muted-foreground">Aucun fichier sélectionné</span>
                        )}
                        {files.map((f, i) => (
                          <span key={i} data-testid={`portal-file-${i}`}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-md bg-muted border">
                            <FileIcon size={12}/>
                            <span className="max-w-[180px] truncate">{f.name}</span>
                            <span className="text-muted-foreground">({(f.size/1024).toFixed(0)} Ko)</span>
                            <button type="button" onClick={() => removeFile(i)}
                              className="text-red-500 hover:bg-red-100 dark:hover:bg-red-950/40 rounded p-0.5"
                              data-testid={`portal-file-remove-${i}`}>
                              <X size={12}/>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button type="submit" disabled={busy} data-testid="portal-submit">
                        <PaperPlaneTilt size={16} className="mr-2"/>{busy ? "Envoi…" : "Envoyer la demande"}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </>
          );

          if (formAlign === "center" || !sideImage) {
            const align = formAlign === "left" ? "justify-start"
              : formAlign === "right" ? "justify-end" : "justify-center";
            return (
              <div className={`flex ${align}`}>
                <div className="w-full max-w-3xl">{formNode}</div>
              </div>
            );
          }
          const imgNode = (
            <div className="hidden lg:flex items-center justify-center">
              <img src={sideImage} alt="Illustration"
                data-testid="portal-side-image"
                className="max-w-full max-h-[75vh] w-full object-cover rounded-xl shadow-lg"/>
            </div>
          );
          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {formAlign === "right" ? imgNode : <div>{formNode}</div>}
              {formAlign === "right" ? <div>{formNode}</div> : imgNode}
            </div>
          );
        })()}
      </main>
      <footer className="mt-auto" data-testid="portal-footer">
        <div className="max-w-6xl mx-auto px-6 py-4 text-center text-xs text-muted-foreground">
          Copyright ©1999-2026 <b>NETKOM INFORMATIQUE</b> — All rights reserved · S.A.S au capital de 127 690 €  · Siret N° 529224602 00027
        </div>
      </footer>
      <ChatWidget onEscalate={onChatEscalate}/>
    </div>
  );
}
